/* eslint-disable react/forbid-prop-types */
import React, { useState, memo, useContext } from 'react';
import { makeStyles } from 'tss-react/mui';
import { alpha } from '@mui/material';
import PropTypes from 'prop-types';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CheckIcon from '@mui/icons-material/ToggleOn';
import Grid from '../../../../components/Grid/Grid';
import Typography from '../../../../components/Typography/Typography';
import useForm from '../../../../hooks/useForm';
import IconButton from '../../../../components/IconButton/IconButton';
import Chip from '../../../../components/Chip/Chip';
import useCancelToken from '../../../../hooks/useCancelToken';
import { deleteMaterialType, editMaterialType } from '../../../../services/settings-config';
import { DialogContext } from '../../../../contexts/DialogContext';
import { dialogActions } from '../../../../reducers/dialogReducer';
import CircularProgress from '../../../../components/CircularProgress/CircularProgress';

const useStyles = makeStyles()((theme) => ({
  iconBtn: {
    width: 26,
    height: 26,
    border: '1px solid',
    marginLeft: theme.spacing(1.25),
    marginTop: 0,
    marginBottom: 0,
  },
  iconBtnIcon: {
    padding: 3.9,
  },
  iconBtnBlock: {
    color: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.warning.light, 0.2),
    },
  },
  iconBtnEnable: {
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.secondary.light, 0.2),
    },
  },
  iconBtnDelete: {
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.light,
    },
  },
  iconBtnSave: {
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  iconBtnCancel: {
    color: theme.palette.grey[600],
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    },
  },
}));

const MaterialTypeConfigRow = ({
  targetMaterial,
  configRow,
  onSaveMaterialType,
  onDeleteMaterialType,
  onDeleteMaterialTypeError,
  onSaveMaterialTypeError,
}) => {
  const { classes } = useStyles();
  const { materialId, materialName } = targetMaterial;
  const { materialTypeName, materialTypeId, isEnabled } = configRow;
  const { newCancelToken } = useCancelToken();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const initialForm = {
    materialTypeName,
    materialTypeId,
    isEnabled,
  };
  const { formData, setFormData } = useForm(initialForm);
  const { dialogDispatch } = useContext(DialogContext);

  const handleEnableMaterialType = () => {
    dialogDispatch({
      type: dialogActions.OPEN,
      config: {
        title: '是否確定要啟用此筆材質種類？',
        message: '是否確認啟用此筆材質種類？後續建立的試燒單將可以使用此材質種類。',
        handleConfirm: async () => {
          const cancelToken = newCancelToken();
          setIsSaving(true);
          await editMaterialType({
            data: {
              materialId,
              materialTypeId: formData.materialTypeId,
              materialTypeName: formData.materialTypeName,
              isEnabled: true,
            },
            cancelToken,
            onSuccess: () => {
              setFormData({
                ...formData,
                isEnabled: true,
              });
              onSaveMaterialType(formData);
            },
            onError: (result, _rtCode, noPrivilegeMsg) => {
              onSaveMaterialTypeError(result, noPrivilegeMsg);
            },
            onSettled: () => {
              setIsSaving(false);
            },
          });
        },
      },
    });
  };

  const handleDisabledMaterialType = () => {
    dialogDispatch({
      type: dialogActions.OPEN,
      config: {
        title: '是否確定要停用此筆材質種類？',
        message: '是否確認停用此筆材質種類？後續建立的試燒單將無法使用此材質種類。',
        handleConfirm: async () => {
          const cancelToken = newCancelToken();
          setIsSaving(true);
          await editMaterialType({
            data: {
              materialId,
              materialTypeId: formData.materialTypeId,
              materialTypeName: formData.materialTypeName,
              isEnabled: false,
            },
            cancelToken,
            onSuccess: () => {
              setFormData({
                materialTypeId: Number(formData.materialTypeId),
                materialTypeName: formData.materialTypeName,
                isEnabled: false,
              });
              onSaveMaterialType(formData);
            },
            onError: (result, _rtCode, noPrivilegeMsg) => {
              onSaveMaterialTypeError(result, noPrivilegeMsg);
            },
            onSettled: () => {
              setIsSaving(false);
            },
          });
        },
      },
    });
  };

  const handleDeleteMaterialType = async () => {
    const cancelToken = newCancelToken();
    setIsDeleting(true);
    await deleteMaterialType({
      data: {
        materialId,
        materialTypeId: formData.materialTypeId,
      },
      cancelToken,
      onSuccess: () => {
        onDeleteMaterialType(materialTypeId);
      },
      onError: (result, _rtCode, noPrivilegeMsg) => {
        onDeleteMaterialTypeError(result, noPrivilegeMsg);
      },
      onSettled: () => {
        setIsDeleting(false);
      },
    });
  };

  const handleDeleteMaterialTypeClick = () => {
    dialogDispatch({
      type: dialogActions.OPEN,
      config: {
        title: '是否確定要刪除此筆材質種類？',
        message: `是否確認刪除材質 [${materialName}] 的 [${formData.materialTypeName}] ？按下確認後，資料將無法復原。`,
        handleConfirm: () => {
          handleDeleteMaterialType();
        },
      },
    });
  };

  return (
    <Grid
      container
      item
      xs={12}
      alignItems="center"
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: 0,
        paddingTop: 1,
        paddingBottom: 1,
        marginBottom: 0.6,
      })}
    >
      <Grid item xs={8} display="flex" alignItems="center" justifyContent="flex-start" gap={2}>
        <Typography variant="subtitle1">{formData.materialTypeName}</Typography>
        <Chip
          color={isEnabled ? 'secondary' : 'default'}
          label={isEnabled ? '啟用' : '停用'}
          size="small"
          sx={(theme) =>
            isEnabled ? { backgroundColor: alpha(theme.palette.secondary.main, 0.6) } : {}
          }
        />
      </Grid>
      <Grid item container justifyContent="flex-end" alignItems="center" xs={4}>
        <>
          {isSaving ? (
            <CircularProgress
              id="saveLoading"
              size={26}
              sx={(theme) => ({ marginLeft: theme.spacing(1) })}
            />
          ) : (
            <IconButton
              className={`${classes.iconBtn} ${
                isEnabled ? classes.iconBtnBlock : classes.iconBtnEnable
              }`}
              aria-label={isEnabled ? 'disable' : 'enable'}
              onClick={isEnabled ? handleDisabledMaterialType : handleEnableMaterialType}
              disabled={isSaving || isDeleting}
            >
              {isEnabled ? (
                <BlockIcon className={classes.iconBtnIcon} />
              ) : (
                <CheckIcon className={classes.iconBtnIcon} />
              )}
            </IconButton>
          )}
          {isDeleting ? (
            <CircularProgress
              id="saveLoading"
              size={26}
              sx={(theme) => ({ marginLeft: theme.spacing(1) })}
            />
          ) : (
            <IconButton
              className={`${classes.iconBtn} ${classes.iconBtnDelete}`}
              aria-label="delete"
              onClick={handleDeleteMaterialTypeClick}
              disabled={isSaving || isDeleting}
            >
              <DeleteIcon className={classes.iconBtnIcon} />
            </IconButton>
          )}
        </>
      </Grid>
    </Grid>
  );
};

MaterialTypeConfigRow.propTypes = {
  configRow: PropTypes.object.isRequired,
  onSaveMaterialType: PropTypes.func.isRequired,
  onSaveMaterialTypeError: PropTypes.func.isRequired,
  onDeleteMaterialType: PropTypes.func.isRequired,
  onDeleteMaterialTypeError: PropTypes.func.isRequired,
  targetMaterial: PropTypes.object.isRequired,
};

export default memo(MaterialTypeConfigRow);
